import React, { useState } from 'react';
import { Shield, FileText, Eye, Lock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import './PoliciesPage.css';

const PoliciesPage: React.FC = () => {
    const { translations, isRTL } = useLanguage();
    const [activeTab, setActiveTab] = useState<'privacy' | 'terms'>('privacy');

    const privacyContent = isRTL ? {
        title: 'سياسة الخصوصية',
        lastUpdated: 'آخر تحديث: 1 يناير 2024',
        sections: [
            {
                title: 'المعلومات التي نجمعها',
                content: 'نحن في يونيكير فارما نحترم خصوصيتك ونلتزم بحماية معلوماتك الشخصية. نجمع المعلومات التي تقدمها لنا بشكل مباشر عند التسجيل في موقعنا أو الاتصال بنا، بما في ذلك اسمك وعنوان بريدك الإلكتروني ورقم هاتفك.'
            },
            {
                title: 'كيف نستخدم معلوماتك',
                content: 'نستخدم المعلومات التي نجمعها لتوفير وتحسين خدماتنا، والتواصل معك بشأن منتجاتنا وخدماتنا، ومعالجة طلباتك واستفساراتك. قد نستخدم معلوماتك أيضاً لإرسال تحديثات وعروض ترويجية، ويمكنك إلغاء الاشتراك في أي وقت.'
            },
            {
                title: 'مشاركة المعلومات',
                content: 'لا نبيع أو نؤجر أو نشارك معلوماتك الشخصية مع أطراف ثالثة لأغراض تسويقية دون موافقتك الصريحة. قد نشارك معلوماتك مع مقدمي الخدمات الموثوقين الذين يساعدوننا في تشغيل موقعنا وتقديم خدماتنا.'
            },
            {
                title: 'أمان البيانات',
                content: 'نتخذ تدابير أمنية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التغيير أو الكشف أو التدمير. نستخدم تشفير SSL وممارسات أمنية متقدمة أخرى لضمان حماية بياناتك.'
            },
            {
                title: 'حقوقك',
                content: 'لديك الحق في الوصول إلى معلوماتك الشخصية وتصحيحها أو حذفها. يمكنك أيضاً طلب تقييد معالجة بياناتك أو الاعتراض على معالجتها. للممارسة هذه الحقوق، يرجى الاتصال بنا باستخدام معلومات الاتصال الواردة أدناه.'
            }
        ]
    } : {
        title: 'Privacy Policy',
        lastUpdated: 'Last updated: January 1, 2025',
        sections: [
            {
                title: 'Information We Collect',
                content: 'At Unicare Pharma, we respect your privacy and are committed to protecting your personal information. We collect information that you provide to us directly when registering on our website or contacting us, including your name, email address, and phone number.'
            },
            {
                title: 'How We Use Your Information',
                content: 'We use the information we collect to provide and improve our services, communicate with you about our products and services, and process your requests and inquiries. We may also use your information to send updates and promotional offers, and you can unsubscribe at any time.'
            },
            {
                title: 'Information Sharing',
                content: 'We do not sell, rent, or share your personal information with third parties for marketing purposes without your explicit consent. We may share your information with trusted service providers who help us operate our website and provide our services.'
            },
            {
                title: 'Data Security',
                content: 'We take appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. We use SSL encryption and other advanced security practices to ensure your data is protected.'
            },
            {
                title: 'Your Rights',
                content: 'You have the right to access, correct, or delete your personal information. You can also request restriction of processing of your data or object to its processing. To exercise these rights, please contact us using the contact information provided below.'
            }
        ]
    };

    const termsContent = isRTL ? {
        title: 'شروط الخدمة',
        lastUpdated: 'آخر تحديث: 1 يناير 2025',
        sections: [
            {
                title: 'قبول الشروط',
                content: 'بالوصول إلى موقع يونيكير فارما واستخدامه، فإنك توافق على الالتزام بهذه الشروط والأحكام وجميع القوانين واللوائح المعمول بها. إذا كنت لا توافق على أي من هذه الشروط، فلا يُسمح لك بالوصول إلى هذا الموقع أو استخدامه.'
            },
            {
                title: 'استخدام الموقع',
                content: 'يمكنك استخدام موقعنا لأغراض مشروعة فقط ووفقاً لهذه الشروط. أنت توافق على عدم استخدام الموقع بأي طريقة قد تضر بالموقع أو تعطله أو تعيق استخدام الآخرين له، أو بأي طريقة غير قانونية أو احتيالية أو ضارة.'
            },
            {
                title: 'المحتوى والملكية الفكرية',
                content: 'جميع المحتويات على هذا الموقع، بما في ذلك النصوص والرسومات والشعارات والصور والبرامج، مملوكة لشركة يونيكير فارما أو مرخصة لها وهي محمية بموجب قوانين حقوق المؤلف والملكية الفكرية.'
            },
            {
                title: 'إخلاء المسؤولية',
                content: 'المعلومات على هذا الموقع مقدمة على أساس "كما هي" دون أي ضمانات من أي نوع. لا تتحمل يونيكير فارما مسؤولية أي أضرار مباشرة أو غير مباشرة قد تنشأ عن استخدام هذا الموقع أو المعلومات الواردة فيه.'
            },
            {
                title: 'التغييرات على الشروط',
                content: 'نحتفظ بالحق في تعديل هذه الشروط والأحكام في أي وقت دون إشعار مسبق. استمرارك في استخدام الموقع بعد نشر أي تغييرات يعتبر موافقة على تلك التغييرات.'
            }
        ]
    } : {
        title: 'Terms of Service',
        lastUpdated: 'Last updated: January 1, 2025',
        sections: [
            {
                title: 'Acceptance of Terms',
                content: 'By accessing and using the Unicare Pharma website, you agree to be bound by these terms and conditions and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from accessing or using this site.'
            },
            {
                title: 'Use of Website',
                content: 'You may use our website for lawful purposes only and in accordance with these terms. You agree not to use the website in any way that could damage, disable, or impair the site or interfere with any other party\'s use of it, or in any way that is unlawful, fraudulent, or harmful.'
            },
            {
                title: 'Content and Intellectual Property',
                content: 'All content on this website, including text, graphics, logos, images, and software, is owned by or licensed to Unicare Pharma and is protected by copyright and intellectual property laws.'
            },
            {
                title: 'Disclaimer',
                content: 'The information on this website is provided on an "as is" basis without any warranties of any kind. Unicare Pharma disclaims all liability for any direct or indirect damages that may arise from the use of this website or the information contained herein.'
            },
            {
                title: 'Changes to Terms',
                content: 'We reserve the right to modify these terms and conditions at any time without prior notice. Your continued use of the website after any changes are posted constitutes acceptance of those changes.'
            }
        ]
    };

    const currentContent = activeTab === 'privacy' ? privacyContent : termsContent;

    return (
        <div className={`policies-page ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="container">
                {/* Page Header */}
                <div className="page-header">
                    <h1>{translations.policies}</h1>
                    <p className="page-description">
                        {isRTL
                            ? 'اقرأ سياسات الخصوصية وشروط الخدمة الخاصة بنا لفهم كيفية حماية بياناتك وحقوقك.'
                            : 'Read our privacy policies and terms of service to understand how we protect your data and your rights.'
                        }
                    </p>
                </div>

                {/* Tab Navigation */}
                <div className="tabs-navigation">
                    <button
                        className={`tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
                        onClick={() => setActiveTab('privacy')}
                    >
                        <Shield size={20} />
                        {translations.privacyPolicy}
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'terms' ? 'active' : ''}`}
                        onClick={() => setActiveTab('terms')}
                    >
                        <FileText size={20} />
                        {translations.termsOfService}
                    </button>
                </div>

                {/* Content */}
                <div className="policy-content">
                    <div className="content-header">
                        <div className="content-icon">
                            {activeTab === 'privacy' ? <Eye size={32} /> : <Lock size={32} />}
                        </div>
                        <div className="content-title">
                            <h2>{currentContent.title}</h2>
                            <p className="last-updated">{currentContent.lastUpdated}</p>
                        </div>
                    </div>

                    <div className="content-body">
                        {currentContent.sections.map((section, index) => (
                            <div key={index} className="content-section">
                                <h3>{section.title}</h3>
                                <p>{section.content}</p>
                            </div>
                        ))}
                    </div>

                    {/* Contact Information */}
                    <div className="contact-section">
                        <h3>
                            {isRTL ? 'تواصل معنا' : 'Contact Us'}
                        </h3>
                        <p>
                            {isRTL
                                ? 'إذا كان لديك أي أسئلة حول هذه السياسات، يرجى التواصل معنا:'
                                : 'If you have any questions about these policies, please contact us:'
                            }
                        </p>
                        <div className="contact-info">
                            <p>
                                <strong>
                                    {isRTL ? 'البريد الإلكتروني:' : 'Email:'}
                                </strong> privacy@unicarepharma.com
                            </p>
                            <p>
                                <strong>
                                    {isRTL ? 'الهاتف:' : 'Phone:'}
                                </strong> +1 (555) 123-4567
                            </p>
                            <p>
                                <strong>
                                    {isRTL ? 'العنوان:' : 'Address:'}
                                </strong> {isRTL
                                    ? 'الرياض حي اشبيليه اعلى هايبر رامز الدور الثالث'
                                    : 'Riyadh, Ishbiliyah District, Above Ramez Hypermarket, 3rd Floor'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PoliciesPage;